import { Client } from "@/Common/Api/Client";
import { Vaccine } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Vaccines";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ISelectLookupControlProps } from "@/Common/Components/Inputs/SelectLookupControl";
import { ITextboxProps } from "@/Common/Components/Inputs/TextboxControl";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ImmunizationDetailEditForm, MedicalItemsImmunizationViewState } from "../MedicalItemsImmunization";
import { ConstantValues } from "./Constants/ConstantValues";
import { ValueSetType } from "./Constants/ValueSetType";
import { CodeValue } from "./Models/CodeValue";
import { MedicationBatchModel } from "./Models/MedicalBatchModel";
import { MedicationModel } from "./Models/MedicationModel";
import { RvqMedicationPreference } from "./Models/RvqMedicationPreference";
import { GetRvqMedicationByTradeNameResponse } from "./ResponseModels/GetRvqMedicationByTradeNameRequest";
import { GetValuesResponse } from "./ResponseModels/GetValuesResponse";
import { ImmunizationDetailsEditComponentProps } from "./ImmunizationDetailsEdit";
import { QuebecVaccinationRegistryHelper } from "./QuebecVaccinationRegistryHelper";

class ImmunizationStateManager {
    private state: MedicalItemsImmunizationViewState;
    private mutateState: (mutation: (state: MedicalItemsImmunizationViewState) => void, then?: () => void) => void;

    public constructor(props: ImmunizationDetailsEditComponentProps) {
        this.state = props.state;
        this.mutateState = props.mutateState;
    }

    // Immunization Name section
    public get ImmunizationNameOptions(): Immutable.List<SearchableItemModelT<any>> {
        return Immutable.List(
            this.isRvqEnabled
                ? this.state.RvqOptions.tradeNames
                : this.state.AvailableVacines
        );
    }

    public onImmunizationNameChange(value: string): void {
        if (this.isRvqEnabled)
            this.mutateState(s => {
                s.ImmunizationName = value;
                s.ImmunizingAgent = "";
                s.QuebecRegistryLotId = null;
                s.LotNumber = "";
                s.QuebecRegistryAgentCode = "";
                s.RvqOptions.batches = [];
                s.showExpiredLotNumbers = false;
            });
        else
            this.onImmunizationAutoComplete(value);
    }

    public get IsImmunizationNameSelected(): boolean {
        return !IsNullOrWhiteSpace(this.state.ImmunizationName)
            && this.state.ImmunizationNameId
            && this.state.RvqOptions.tradeNames.any(x => x.Item.DisplayText === this.state.ImmunizationName);
    }

    private get isImmunizationNameFilled(): boolean {
        return this.state.ImmunizationName?.trim().length > 0;
    }

    public get isRefuseSpecificVaccineForm(): boolean {
        return this.state.currentForm === ImmunizationDetailEditForm.RefuseSpecificVaccine;
    }

    public get isCreateImmunizationForm(): boolean {
        return this.state.currentForm === ImmunizationDetailEditForm.CreateImmunization;
    }

    public get isRefuseAllVaccinesForm(): boolean {
        return this.state.currentForm === ImmunizationDetailEditForm.RefuseAllVaccines;
    }

    public get isImmunizationNameValid(): boolean {
        if (!this.isRvqEnabled) {
            return this.isImmunizationNameFilled;
        }

        if (this.state.shouldSyncWithRvq)
            return this.state.IsQuebecRegistryHistoricalVaccine
                || (!!this.state.ImmunizationName && this.state.RvqOptions.tradeNames?.any(x => x.Item.Id === this.state.ImmunizationName))
                || this.isRefuseSpecificVaccineForm;

        return this.agentFieldIsSet || this.isImmunizationNameFilled;
    }

    private get agentFieldIsSet(): boolean {
        return !!this.state.ImmunizingAgent?.trim();
    }

    public onImmunizationAutoComplete(code: string, rvqPreference: RvqMedicationPreference = null): void {
        if (this.isRvqEnabled) {
            this.setRvqMedicationByTradeName(code, rvqPreference);
        }
        else
            this.mutateState(s => {
                s.ImmunizationName = code;
                const foundVac = cacheStore.VaccinesCache.firstOrDefault(x => Vaccine.GetLocalizedName(x) == code);
                if (foundVac) {
                    s.ImmunizingAgent = Vaccine.GetLocalizedImmunogen(foundVac);
                    s.Manufacturer = foundVac.Manufacturer;
                    s.RouteCode = foundVac.PreferredRoute;
                    s.Dose = foundVac.DefaultDosage;
                    s.SelectedDoseUnitLookupId = foundVac.DefautlDosageUnit != null
                        ? cacheStore.FindLookupByLookupCode(foundVac.DefautlDosageUnit).LookupID
                        : s.SelectedDoseUnitLookupId;
                }
            });
    }

    private async setRvqMedicationByTradeName(tradeName: string, rvqPreference: RvqMedicationPreference = null): Promise<void> {
        const medication = await this.getRvqMedicationByTradeName(tradeName);
        const codeValues = await this.getAntigensByTradeName(tradeName);

        const batches = medication?.Batches ?? [];

        this.mutateState(
            state => {
                state.ImmunizationName = medication?.TradeName ?? tradeName;
                state.ImmunizationNameId = medication?.TradeName ?? tradeName;
                state.Manufacturer = medication?.ManufacturerCode;
                state.ImmunizingAgent = QuebecVaccinationRegistryHelper.translateValueSetString(medication?.Agent, ValueSetType.Agent);
                state.QuebecRegistryAgentCode = medication?.Agent.Code;
                state.LotNumber = "";
                state.RvqOptions.batches = batches.sortByProperty(x => x.LotNumber);
                state.showExpiredLotNumbers = batches.all(x => x.IsExpired);

                state.SelectedAntigens = codeValues;
                state.AntigensInCurrentAgent = codeValues;
                state.codeValueSearchStrategy = QuebecVaccinationRegistryHelper.getCodeValueSearchStrategy(codeValues, "RvqCodeValueAntigensFromSingleAgentSearch", ValueSetType.Antigen);

                if (this.state.IsQuebecRegistryHistoricalVaccine)
                    this.onHistoricalValueStateChange(state.IsQuebecRegistryHistoricalVaccine, state);
            },
            () => {
                const batch = batches.firstOrDefault(x => x.Id === rvqPreference?.LotId)
                    ?? (batches.length === 1 && batches[0]);

                if (batch)
                    this.onLotNumberAutoComplete(null, batch, null);
            }
        );
    }

    private async getRvqMedicationByTradeName(tradeName: string): Promise<MedicationModel> {
        if (tradeName && this.state.RvqOptions?.tradeNames.some(x => x.Item.Id === tradeName)) {
            const response = await Client.postAsync<GetRvqMedicationByTradeNameResponse>(
                ServerOperations.Server.QuebecVaccinationRegistry_GetMedicationByTradeName,
                {
                    TradeName: tradeName
                }
            );

            if (response.errors.any() || response.data?.Issues?.any())
                sessionStore.showPopup(response.errors.map(p => p.Message).concat(response.data?.Issues ?? []));

            if (response.isSuccess)
                return response?.data?.Medication;
        }
    }

    // Immunization Agent/Type section
    public ImmunizationAgentProps(): Partial<ITextboxProps> {
        if (this.isRvqEnabled)
            return {
                onAutoCompleteSelectCallBack: (code: string, _, value: string) => this.mutateState(
                    s => {
                        s.QuebecRegistryAgentCode = code;
                        s.ImmunizingAgent = value;
                    },
                    () => this.setAntigensByAgentNameOrCode(value, code)
                ),
                autoCompleteItems: SearchableItemModelT.fromObject(QuebecVaccinationRegistryHelper.getTranslatedDictionary(this.state.RvqOptions.agents, ValueSetType.Agent)).toImmutable(),
                limit: null,
                listMaxHeight: "241px",
                valid: this.isImmunizingAgentValid

            };
        else
            return {};
    }

    public get isImmunizingAgentValid(): boolean {
        if (!this.isRvqEnabled)
            return true;

        if (this.state.shouldSyncWithRvq)
            return Object.values(this.state.RvqOptions.agents).any(value => value === this.state.ImmunizingAgent);

        return this.isImmunizationNameFilled || this.agentFieldIsSet;
    }

    public async setAntigensByAgentNameOrCode(immunizingAgentName?: string, immunizingAgentCode?: string): Promise<void> {
        const agentCode = immunizingAgentCode ?? this.getAgentCodeByAgentName(immunizingAgentName);
        if (!agentCode)
            return;

        const codeValues = await QuebecVaccinationRegistryHelper.getAntigensByAgentCode(agentCode);

        this.mutateState(s => {
            s.QuebecRegistryAgentCode = agentCode;
            s.SelectedAntigens = codeValues;
            s.AntigensInCurrentAgent = codeValues;
            s.codeValueSearchStrategy = QuebecVaccinationRegistryHelper.getCodeValueSearchStrategy(codeValues, "RvqCodeValueAntigensFromSingleAgentSearch", ValueSetType.Antigen);
        });
    }

    private getAgentCodeByAgentName(agentName: string): string | null {
        return Object.values(this.state.RvqOptions.agents)?.firstOrDefault(a => a.Value === agentName)?.Code;
    }

    private async getAntigensByTradeName(tradeName: string): Promise<CodeValue[]> {
        const response = await Client.postAsync<GetValuesResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_GetAntigensByTradeName,
            {
                TradeName: tradeName
            }
        );

        return QuebecVaccinationRegistryHelper.getCodeValues(KeyValuePair.fromMapOfStrings(QuebecVaccinationRegistryHelper.getTranslatedDictionary(response.data.Antigens, ValueSetType.Antigen)));
    }

    public get isAdministrationReasonValid(): boolean {
        return !this.state.shouldSyncWithRvq
            || (
                this.isAdministrationReasonRequired
                && !!this.state.QuebecRegistryAdministeredReasonCode
            );
    }

    public get isAdministrationReasonRequired(): boolean {
        return !!this.state.QuebecRegistryAgentCode
            && this.state.RvqOptions.agents?.[this.state.QuebecRegistryAgentCode]?.NeedsAdministrationReason;
    }

    public get isLocationValid(): boolean {
        return !this.state.shouldSyncWithRvq
            || this.state.IsQuebecRegistryHistoricalVaccine
            || (
                !!this.state.QuebecRegistryLocationId
                && !!this.state.LocationDisplayText
            );
    }

    public get isAdministeredByValid(): boolean {
        return !this.state.shouldSyncWithRvq
            || this.state.IsQuebecRegistryHistoricalVaccine
            || (
                !!this.state.AdministeredByDisplayText
                && !!this.state.QuebecRegistryAdministeredById
            );
    }

    public get lotNumberItems(): MedicationBatchModel[] {
        return this.isRvqEnabled
            ? this.state.RvqOptions.batches
            : null;
    }

    public get onLotNumberAutoComplete(): (Code: string, Model: any, Value: string) => void {
        if (this.isRvqEnabled)
            return (code: string, model: MedicationBatchModel) => this.mutateState(s => {
                s.Dose = model.DefaultQuantity;
                s.QuebecRegistryDoseUnitCode = model.DefaultQuantityUnit.Code;
                s.RouteCode = model.DefaultRoute.Code;
                s.QuebecRegistryLotId = model.Id;
                s.LotNumber = MedicationBatchModel.getLotDisplayName(model);
                s.QuebecRegistryExpirationDate = model.ExpirationDate;
            });

        return undefined;
    }

    public get isLotNumberValid(): boolean {
        const isLotNumberNotEmptyAndPartOfValidList = !!this.state.LotNumber
            && !!this.state.QuebecRegistryLotId
            && this.lotNumberItems?.any(x => x.Id === this.state.QuebecRegistryLotId);

        const isLotNumberEmpty = !this.state.LotNumber && !this.state.QuebecRegistryLotId;
        if (!this.state.IsQuebecRegistryHistoricalVaccine)
            return isLotNumberNotEmptyAndPartOfValidList || !this.state.shouldSyncWithRvq;

        return this.state.IsQuebecRegistryHistoricalVaccine
            && (isLotNumberEmpty || isLotNumberNotEmptyAndPartOfValidList)
            || !this.state.shouldSyncWithRvq;
    }

    public get routeOptions(): KeyValuePair<string, string>[] {
        return this.isRvqEnabled
            ? KeyValuePair.fromMapOfStrings(QuebecVaccinationRegistryHelper.getTranslatedDictionary(this.state.RvqOptions?.routes, ValueSetType.AdministrationRoute))
            : this.state.AvailableRouteLookups;
    }

    public get isRouteValid(): boolean {
        return !this.isRvqEnabled
            || !!this.state.RouteCode
            || !this.state.shouldSyncWithRvq;
    }

    public get siteOptions(): ComboBoxDropDownItem[] {
        if (this.isRvqEnabled)
            return ComboBoxDropDownItem.fromDictionary(QuebecVaccinationRegistryHelper.getTranslatedDictionary(this.state.RvqOptions?.administrationSites, ValueSetType.AdministrationSite));
        else
            return this.state.AvailableSitesLookups?.map(x => x.ToComboboxItem());
    }

    public createSiteProps(): ISelectLookupControlProps {
        return {
            Value: this.isRvqEnabled ? this.state.QuebecRegistrySiteCode : this.state.SelectedSiteLookupId,
            IncludeEmptyChoice: true,
            AvailableValues: this.siteOptions,
            Callback: siteLookUpId => this.onSiteSelect(siteLookUpId),
            invalid: !this.isSiteValid
        };
    }

    public onSiteSelect(siteLookUpId: any): void {
        this.mutateState(s => {
            if (this.isRvqEnabled)
                s.QuebecRegistrySiteCode = siteLookUpId;
            else
                s.SelectedSiteLookupId = siteLookUpId;
        });
    }

    public get isSiteValid(): boolean {
        return !this.isRvqEnabled
            || !!this.state.QuebecRegistrySiteCode
            || !this.state.shouldSyncWithRvq;
    }

    public get selectedDoseUnit(): string {
        return this.isRvqEnabled
            ? this.state.QuebecRegistryDoseUnitCode
            : this.state.SelectedDoseUnitLookupId;
    }

    public get doseUnitOptions(): ComboBoxDropDownItem[] {
        return this.isRvqEnabled
            ? ComboBoxDropDownItem.fromDictionary(QuebecVaccinationRegistryHelper.getTranslatedDictionary(this.state.RvqOptions?.dosageUnits, ValueSetType.DosageUnit))
            : this.state.AvailableDoseUnitLookups?.sortByProperty(x => x.GetValue())
                .map(x => x.ToComboboxItem()) ?? [];
    }

    public get isDoseQuantityValid(): boolean {
        if (!this.isRvqEnabled || !this.state.shouldSyncWithRvq)
            return true;

        const isDoseGreaterThanZero = this.state.Dose > 0;
        const isDoseUnitFilled = !!this.state.QuebecRegistryDoseUnitCode;

        if (!this.state.IsQuebecRegistryHistoricalVaccine)
            return isDoseGreaterThanZero && isDoseUnitFilled;

        return isDoseGreaterThanZero
            ? isDoseUnitFilled
            : IsNullOrUndefined(this.state.Dose) && this.state.QuebecRegistryDoseUnitCode === ConstantValues.UnknownDoseUnitCode;
    }

    public get isDoseUnitValid(): boolean {
        return !this.isRvqEnabled
            || this.state.IsQuebecRegistryHistoricalVaccine
            || !this.state.shouldSyncWithRvq
            || !!this.selectedDoseUnit;
    }

    public onDoseUnitChange(doseUnitCode: string): void {
        this.mutateState(s => {
            if (this.isRvqEnabled) {
                s.QuebecRegistryDoseUnitCode = doseUnitCode;
                if (doseUnitCode === ConstantValues.UnknownDoseUnitCode)
                    s.Dose = null;
            }
            else
                s.SelectedDoseUnitLookupId = doseUnitCode;
        });
    }

    private get isRvqEnabled(): boolean {
        return cacheStore.ClinicSettings.EnableQuebecVaccinationRegistry ?? false;
    }

    private getDefaultValue<TValue>(isHistorical: boolean, current: TValue, historicalDefault: TValue, normalDefault: TValue = null): TValue {
        if (isHistorical)
            return current || historicalDefault;

        return current === historicalDefault
            ? normalDefault
            : current;
    }

    public onHistoricalValueStateChange(isHistoricalVaccine: boolean, state: MedicalItemsImmunizationViewState): void {
        const getDefaultValue = <TValue extends unknown>(current: TValue, historicalDefault: TValue, normalDefault: TValue = null): TValue => this.getDefaultValue(isHistoricalVaccine, current, historicalDefault, normalDefault);

        state.QuebecRegistryDoseUnitCode = state.QuebecRegistryDoseUnitCode ?? "";
        state.RouteCode = state.RouteCode ?? "";
        state.QuebecRegistrySiteCode = state.QuebecRegistrySiteCode ?? "";
        state.QuebecRegistryAdministeredById = getDefaultValue(state.QuebecRegistryAdministeredById, ConstantValues.PractitionerUnknownId);
        state.QuebecRegistryLocationId = getDefaultValue(state.QuebecRegistryLocationId, ConstantValues.LocationUnknownId);

        if (isHistoricalVaccine) {
            state.AdministeredByDisplayText = !!state.QuebecRegistryAdministeredById ? state.AdministeredByDisplayText : "";
            state.LocationDisplayText = !!state.QuebecRegistryLocationId ? state.LocationDisplayText : "";
        }
        else {
            state.AdministeredByDisplayText = state.QuebecRegistryAdministeredById === ConstantValues.PractitionerUnknownId ? "" : state.AdministeredByDisplayText;
            state.LocationDisplayText = state.QuebecRegistryLocationId === ConstantValues.LocationUnknownId ? "" : state.LocationDisplayText;
        }
    }
}

export { ImmunizationStateManager };
